<template>
  <b-container>
    <b-modal
      v-model="customEditVisible"
      title="write a custom view"
      size="xl"
      scrollable
      modal-class="view-editor-modal custom-view-wizard"
      @hide="pendingExampleId = ''"
    >
      <template #title>
        write a custom view
        <modal-help topic="edit-custom" />
      </template>
      <div class="custom-wizard">
        <div class="custom-naming">
          <label class="field-label">
            title
            <b-form-input v-model="title" placeholder="view title" required />
          </label>
          <label class="field-label">
            unique title
            <b-form-input v-model="uniqueTitle" placeholder="view unique title" required />
          </label>
        </div>

        <ol class="custom-steps">
          <li v-for="(label, index) in stepLabels" :key="label">
            <button
              type="button"
              :class="{ 'is-current': step === index }"
              :disabled="!canOpen(index)"
              @click="step = index"
            >
              {{ index + 1 }}. {{ label }}
            </button>
          </li>
        </ol>

        <section v-if="step === 0" class="custom-step">
          <p class="section-desc">Start from an example. You can change every line on the next step. Examples use the columns on {{ dataSourceLabel }}.</p>
          <div class="example-grid">
            <button
              v-for="example in examples"
              :key="example.id"
              type="button"
              class="example-card"
              :class="{ 'is-selected': exampleId === example.id }"
              @click="selectExample(example)"
            >
              <span class="example-name">{{ example.label }}</span>
              <span class="example-description">{{ example.description }}</span>
            </button>
          </div>
          <div v-if="pendingExample" class="replace-note">
            <p>Using {{ pendingExample.label }} replaces the code you have now.</p>
            <button type="button" class="wizard-btn" @click="applyExample(pendingExample)">replace code</button>
            <button type="button" class="wizard-btn wizard-btn-quiet" @click="pendingExampleId = ''">keep my code</button>
          </div>
          <p v-else-if="!codeOk" class="section-desc">Pick an example to continue. Blank is there when you want to start from a short note.</p>
        </section>

        <section v-else-if="step === 1" class="custom-step">
          <p class="section-desc">This is HTML. A field tag inserts that column as text. The buttons below insert a tag at the cursor.</p>
          <div class="field-chips">
            <button
              v-for="field in fieldChoices"
              :key="field.uniqueTitle"
              type="button"
              class="field-chip"
              @click="insertField(field.uniqueTitle)"
            >
              {{ field.title }}
            </button>
          </div>
          <p v-if="!fieldChoices.length" class="section-desc">This spreadsheet has no text or number columns yet. You can still write HTML, and the count tag in the list below tells you how many records loaded.</p>
          <label class="field-label">
            view code
            <textarea ref="codeInput" v-model="viewCode" class="form-control custom-code" rows="10" spellcheck="false"></textarea>
          </label>
          <p v-if="codeMessage" class="code-error">{{ codeMessage }}</p>
          <label class="field-label">
            CSS for this view
            <textarea v-model="viewCss" class="form-control custom-code" rows="10" spellcheck="false"></textarea>
          </label>
          <p class="section-desc">Selectors apply inside this view. <code>.custom-record</code> styles an element with that class in the code above.</p>
          <ViewAiPanel
            v-model="aiPrompt"
            :error="aiError || ''"
            :submitting="aiSubmitting"
            submitting-text="Writing the view and checking the preview... this can take a few minutes."
            heading="Write with AI"
            description="Describe the view you want. The AI writes the HTML and CSS, looks at the preview, and revises the code when the picture does not match."
            placeholder="e.g. a card for each record with the name in large type and the city underneath"
          />
          <button type="button" class="wizard-btn custom-ai-write" :disabled="aiSubmitting" @click="writeWithAi">
            {{ aiSubmitting ? 'writing...' : 'write with AI' }}
          </button>
          <ul class="syntax-list">
            <li v-for="item in syntax" :key="item.label">
              <code>{{ item.label }}</code>
              <span>{{ item.detail }}</span>
            </li>
          </ul>
        </section>

        <section v-else-if="step === 2" class="custom-step">
          <p class="section-desc">The live view loads up to 100 records from {{ dataSourceLabel }}. Search and filters narrow that set before the code runs.</p>
          <label v-if="pageRecordLink" class="checkbox-label">
            <input type="checkbox" v-model="filterByPageRecord" />
            only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
          </label>
          <p v-if="pageRecordLink" class="section-desc">This page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. The view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
          <label class="checkbox-label">
            <input type="checkbox" v-model="realtime" />
            refresh this view when records change
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="searchEnabled" />
            show a search box
          </label>
          <div v-if="searchEnabled" class="search-scope">
            <label class="checkbox-label">
              <input type="radio" value="view" v-model="searchScope" />
              search the fields named in the view code
            </label>
            <label class="checkbox-label">
              <input type="radio" value="spreadsheet" v-model="searchScope" />
              search every field on the spreadsheet
            </label>
            <p v-if="searchScope === 'spreadsheet'" class="section-desc">Searching every field matches columns that are not in the view code. A visitor can learn that a hidden value exists. Password fields are never searched.</p>
          </div>
          <label class="checkbox-label">
            <input type="checkbox" v-model="userFiltering" />
            allow visitors to filter records
          </label>
          <label v-if="userFiltering" class="checkbox-label">
            <input type="checkbox" v-model="restrictUserFiltering" />
            limit the filter list to fields named in the view code
          </label>
          <div v-if="userFiltering" class="filter-block">
            <div class="section-heading">Default filters</div>
            <p class="section-desc">These filters are shown when the view opens. Visitors can change them.</p>
            <PreFilterBuilder
              :filters="defaultUserFilters"
              :match-mode="defaultUserFilterMatchMode"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:filters="defaultUserFilters = $event"
              @update:match-mode="defaultUserFilterMatchMode = $event"
            />
          </div>
          <div class="filter-block">
            <div class="section-heading">Prefilters</div>
            <p class="section-desc">These filters always apply. Visitors do not see them.</p>
            <PreFilterBuilder
              :filters="preFilters"
              :match-mode="preFilterMatchMode"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:filters="preFilters = $event"
              @update:match-mode="preFilterMatchMode = $event"
            />
          </div>
          <FilterButtonEditor
            :buttons="filterButtons"
            :allow-multiple="allowMultipleFilterButtons"
            :spreadsheet="spreadsheet"
            :all-spreadsheets="allSpreadsheets"
            :application-unique-title="applicationUniqueTitle"
            @update:buttons="filterButtons = $event"
            @update:allow-multiple="allowMultipleFilterButtons = $event"
          />
        </section>

        <section v-else class="custom-step">
          <p class="section-desc">This preview uses three sample records, not the rows saved on the spreadsheet. The live page uses the real records.</p>
          <p v-if="codeMessage" class="code-error">{{ codeMessage }}</p>
          <div v-else :data-view-id="previewDomId" class="custom-preview-stage" v-html="previewDocument"></div>
        </section>
      </div>
      <template #footer>
        <button type="button" class="wizard-btn wizard-btn-quiet" @click="customEditVisible = false">cancel</button>
        <button type="button" class="wizard-btn wizard-btn-quiet" :disabled="step === 0" @click="step -= 1">back</button>
        <button v-if="step < 3" type="button" class="wizard-btn" :disabled="!canAdvance" @click="step += 1">next</button>
        <button v-else type="button" class="wizard-btn" :disabled="!canSave" @click="save">save view</button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import PreFilterBuilder from './lib/PreFilterBuilder.vue'
import FilterButtonEditor from './lib/FilterButtonEditor.vue'
import ViewAiPanel from './ViewAiPanel.vue'
import { pageRecordRelationship } from '../lib/page-record-link.js'
import { viewAiErrorMessage } from '../lib/view-ai.js'
import {
  customViewDomId,
  customViewExamples,
  customViewFieldNames,
  customViewInsertFields,
  customViewReady,
  customViewScopedCss,
  customViewSyntax,
  renderCustomView,
  sampleCustomViewContext,
  sanitizePreviewHtml
} from '../../../shared/custom-view.js'
import { sanitizeStyleText } from '../../../shared/html-sanitize.js'

export default {
  name: 'customEdit',
  components: { PreFilterBuilder, FilterButtonEditor, ViewAiPanel },
  data () {
    return {
      customEditVisible: false,
      step: 0,
      stepLabels: ['Example', 'Write', 'Records', 'Preview'],
      uniqueTitle: '',
      title: '',
      spreadSheetUniqueTitle: '',
      spreadsheet: null,
      allSpreadsheets: [],
      viewCode: '',
      viewCss: '',
      exampleId: '',
      pendingExampleId: '',
      realtime: false,
      userFiltering: false,
      searchEnabled: false,
      searchScope: 'view',
      restrictUserFiltering: false,
      defaultUserFilters: [],
      defaultUserFilterMatchMode: 'all',
      preFilters: [],
      preFilterMatchMode: 'all',
      filterButtons: [],
      allowMultipleFilterButtons: false,
      parentDesigner: null,
      filterByPageRecord: false,
      pageRecordSpreadSheetUniqueTitle: '',
      relationshipColumnTitle: '',
      applicationUniqueTitle: '',
      aiPrompt: '',
      aiError: '',
      aiSubmitting: false
    }
  },
  computed: {
    syntax () {
      return customViewSyntax()
    },
    examples () {
      return customViewExamples(this.spreadsheet && this.spreadsheet.columns)
    },
    fieldChoices () {
      return customViewInsertFields(this.spreadsheet, this.allSpreadsheets)
    },
    pendingExample () {
      return this.examples.find((example) => example.id === this.pendingExampleId) || null
    },
    codeCheck () {
      return customViewReady(this.viewCode)
    },
    codeOk () {
      return this.codeCheck.ok
    },
    codeMessage () {
      return this.codeOk ? '' : this.codeCheck.error
    },
    canAdvance () {
      if (this.aiSubmitting) return false
      return this.step === 2 || this.codeOk
    },
    canSave () {
      return this.codeOk && String(this.title || '').trim() && String(this.uniqueTitle || '').trim()
    },
    pageRecordLink () {
      return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
    },
    dataSourceLabel () {
      return (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'this spreadsheet'
    },
    previewDomId () {
      return customViewDomId(this.uniqueTitle || 'custom_preview')
    },
    previewCss () {
      return customViewScopedCss(this.uniqueTitle || 'custom_preview', this.viewCss)
    },
    previewHtml () {
      if (!this.codeOk) return ''
      const columns = [].concat((this.spreadsheet && this.spreadsheet.columns) || [])
      for (const name of customViewFieldNames(this.viewCode)) {
        if (name.startsWith('rel.') || name.startsWith('rev.')) {
          columns.push({ uniqueTitle: name, title: name.split('.').pop(), type: 'text' })
        }
      }
      const context = sampleCustomViewContext(columns, this.title)
      return renderCustomView(this.viewCode, context).html
    },
    previewDocument () {
      const css = this.previewCss ? `<style>${sanitizeStyleText(this.previewCss)}</style>` : ''
      return css + sanitizePreviewHtml(this.previewHtml)
    }
  },
  methods: {
    canOpen (index) {
      if (index <= this.step) return true
      return this.codeOk
    },
    selectExample (example) {
      const current = String(this.viewCode || '')
      if (!current.trim() || current === example.code) {
        this.applyExample(example)
        return
      }
      this.pendingExampleId = example.id
    },
    applyExample (example) {
      this.viewCode = example.code
      this.viewCss = example.css
      this.exampleId = example.id
      this.pendingExampleId = ''
    },
    insertField (uniqueTitle) {
      const token = `{{${uniqueTitle}}}`
      const el = this.$refs.codeInput
      const code = this.viewCode || ''
      if (!el) {
        this.viewCode = code + token
        return
      }
      const start = el.selectionStart || 0
      const end = el.selectionEnd || 0
      this.viewCode = code.slice(0, start) + token + code.slice(end)
      this.$nextTick(() => {
        el.focus()
        const pos = start + token.length
        el.setSelectionRange(pos, pos)
      })
    },
    async getSpreadSheet () {
      const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
        headers: { 'application-unique-title': this.applicationUniqueTitle }
      })
      this.spreadsheet = spreadsheet.data
    },
    async showEditCustomComponent () {
      this.pendingExampleId = ''
      this.aiPrompt = ''
      this.aiError = ''
      this.aiSubmitting = false
      this.step = String(this.viewCode || '').trim() ? 1 : 0
      try {
        await this.getSpreadSheet()
      } catch (err) {
        console.warn('Failed to load spreadsheet for custom view', err)
        this.spreadsheet = null
      }
      this.customEditVisible = true
    },
    draftPayload () {
      return {
        uniqueTitle: this.uniqueTitle,
        title: this.title,
        spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
        components: [],
        viewCode: this.viewCode,
        viewCss: this.viewCss,
        exampleId: this.exampleId || '',
        userFiltering: this.userFiltering,
        searchEnabled: !!this.searchEnabled,
        searchScope: this.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view',
        restrictUserFiltering: this.restrictUserFiltering,
        defaultUserFilters: this.defaultUserFilters,
        defaultUserFilterMatchMode: this.defaultUserFilterMatchMode,
        preFilters: this.preFilters,
        preFilterMatchMode: this.preFilterMatchMode,
        filterButtons: this.filterButtons,
        allowMultipleFilterButtons: !!this.allowMultipleFilterButtons,
        realtime: this.realtime,
        filterByPageRecord: !!this.filterByPageRecord
      }
    },
    async persistDraft () {
      if (!this.parentDesigner) return
      await this.parentDesigner.saveActiveComponent(this.draftPayload())
    },
    save () {
      if (!this.parentDesigner || !this.canSave) return
      this.persistDraft()
      this.customEditVisible = false
    },
    async writeWithAi () {
      const prompt = String(this.aiPrompt || '').trim()

      if (!prompt) {
        this.aiError = 'Describe the view you want.'
        return
      }

      if (String(this.viewCode || '').trim() && !this.codeOk) {
        this.aiError = this.codeMessage
        return
      }

      const page = this.parentDesigner && this.parentDesigner.page
      const pageUniqueTitle = page && page.uniqueTitle

      if (!pageUniqueTitle || !this.applicationUniqueTitle) {
        this.aiError = 'Open a page before writing this view.'
        return
      }

      this.aiError = ''
      this.aiSubmitting = true

      try {
        if (this.codeOk) await this.persistDraft()

        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/ai-views/edit`,
          {
            pageUniqueTitle,
            viewUniqueTitle: this.uniqueTitle,
            prompt
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'application-unique-title': this.applicationUniqueTitle
            },
            timeout: 600000
          }
        )
        const view = response && response.data && response.data.view
        const savedPage = response && response.data && response.data.page

        if (savedPage && this.parentDesigner) {
          this.parentDesigner.loadPage(savedPage)
          const saved = this.parentDesigner.findComponentByUniqueTitle(
            this.parentDesigner.page,
            (view && view.uniqueTitle) || this.uniqueTitle
          )
          if (saved) this.parentDesigner.activeComponent = saved
        }

        if (view && typeof view.viewCode === 'string') {
          this.viewCode = view.viewCode
          this.viewCss = typeof view.viewCss === 'string' ? view.viewCss : this.viewCss
          if (view.title) this.title = view.title
          if (view.uniqueTitle) this.uniqueTitle = view.uniqueTitle
          this.exampleId = ''
        }

        this.aiPrompt = ''
      } catch (error) {
        console.error('AI custom view update failed:', error)
        this.aiError = viewAiErrorMessage(error)
      }

      this.aiSubmitting = false
    }
  }
}
</script>

<style>
.view-editor-modal.custom-view-wizard .modal-dialog {
  max-width: 960px;
}
.custom-naming {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
.custom-steps {
  display: flex;
  gap: 8px;
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
}
.custom-steps button {
  border: 1px solid #3a3a38;
  background: transparent;
  color: #b0b0ae;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.78rem;
}
.custom-steps button.is-current {
  color: #141413;
  background: #9ad12b;
  border-color: #9ad12b;
}
.custom-steps button:disabled {
  opacity: 0.45;
}
.example-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.example-card {
  text-align: left;
  border: 1px solid #3a3a38;
  background: #141413;
  color: #e8e8e6;
  border-radius: 8px;
  padding: 10px 12px;
}
.example-card.is-selected {
  border-color: #9ad12b;
}
.example-name {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}
.example-description {
  display: block;
  color: #8a8a88;
  font-size: 0.82rem;
}
.field-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.field-chip,
.wizard-btn {
  border: 1px solid #3a3a38;
  background: #232320;
  color: #e8e8e6;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.78rem;
}
.wizard-btn {
  margin-left: 8px;
}
.wizard-btn:disabled {
  opacity: 0.45;
}
.wizard-btn-quiet {
  background: transparent;
  color: #b0b0ae;
}
.custom-ai-write {
  margin: 4px 0 14px;
}
textarea.form-control.custom-code {
  height: auto;
  min-height: 15.5em;
  resize: vertical;
  line-height: 1.45;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.85rem;
  background: #141413;
  color: #f3f3f1;
  border: 1px solid #3a3a38;
}
.code-error {
  color: #f82471;
  font-size: 0.85rem;
}
.syntax-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}
.syntax-list li {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 0.82rem;
  color: #b0b0ae;
}
.syntax-list code {
  color: #9ad12b;
}
.filter-block {
  margin: 8px 0 12px;
}
.replace-note {
  margin-top: 12px;
  color: #e8e8e6;
}
.custom-preview-stage {
  background: #fff;
  color: #1c1c1a;
  border-radius: 8px;
  padding: 16px;
  min-height: 160px;
}
.custom-preview-stage h1,
.custom-preview-stage h2,
.custom-preview-stage h3,
.custom-preview-stage h4,
.custom-preview-stage p,
.custom-preview-stage li,
.custom-preview-stage dt,
.custom-preview-stage dd {
  color: #1c1c1a;
}
</style>
